import * as React from "react";
import { IRcaTestResultInsight } from "../models/IRcaTestResultInsight";
import { IRcaTestResult, RcaTestStatus } from "../models/IRcaTestResult";
import { iconNames, getIconColors } from "./TestResultNode";
import { Depths, Stack, Text, Icon, mergeStyles, useTheme } from "@fluentui/react";
import ReactMarkdown, { ExtraProps } from "react-markdown";

const noMarginStyle = mergeStyles({ margin: 0 });
const marginBottomStyle = mergeStyles({ margin: 0, marginBottom: 10 });

/**
 * Returns a string for the icon labels.
 *
 * @param status - The test status.
 * @returns - The string for the label.
 */
export const insightIconLabels = (status: RcaTestStatus): string => {
  switch (status) {
    case "Invalid":
    case "Error":
    case "FatalError":
      return ClientStrings.InsightError;

    case "SuccessWithWarnings":
    case "Warning":
      return ClientStrings.InsightWarning;

    case "Informational":
      return ClientStrings.InsightInformation;

    case "Success":
      return ClientStrings.InsightSuccess;

    default:
      return "";
  }
};

/**
 * Custom markdown components for rendering markdown content with customized HTML.
 */
const markdownComponents = {
  /**
   * Removes margin from paragraphs which wraps the whole markdown content, so we can control the spacing.
   *
   * @param props - The paragraph element props with node destructured out.
   * @returns A paragraph element with no margin.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- node is required to be destructured to remove it from props, otherwise it will pass down to the p tag
  p: ({ node, ...props }: React.ComponentPropsWithoutRef<"p"> & ExtraProps) => <p {...props} style={{ ...props.style, margin: 0 }} />,
  /**
   * Opens links from markdown content in a new tab.
   *
   * @param props - The anchor element props including href and children.
   * @returns An anchor element configured to open in a new tab.
   */
  a: ({ href, children }: React.ComponentPropsWithoutRef<"a">) => (
    <a target="_blank" rel="noopener noreferrer" href={href}>
      {children}
    </a>
  ),
};

/**
 * Component to show the insights.
 *
 * @param currentResult - The result.
 * @returns - The ReactElement with insights.
 */
export const TestResultInsights: React.FC<{
  /**
   * The test result
   */
  currentResult: IRcaTestResult;
}> = ({ currentResult }) => {
  const theme = useTheme();
  const allInsights = FlattenInsights(currentResult);
  return allInsights.length === 0 ? (
    <></>
  ) : (
    <Stack>
      <h2 className={marginBottomStyle}>
        <Text
          styles={{
            root: { fontSize: theme.fonts.xLarge.fontSize, fontWeight: "bold" },
          }}
        >
          {WebsiteStrings.TestResultTable_Insights}
        </Text>
      </h2>
      <Stack>
        {allInsights.map((insight, index) => (
          <TestResultInsight key={index} currentInsight={insight} />
        ))}
      </Stack>
      <h2 className={marginBottomStyle}>
        <Text
          styles={{
            root: { fontSize: theme.fonts.xLarge.fontSize, fontWeight: "bold" },
          }}
        >
          {WebsiteStrings.TestResultTable_Details}
        </Text>
      </h2>
    </Stack>
  );
};

/**
 * Component to show the insight.
 *
 * @param currentResult - The result.
 * @returns - The ReactElement with insights.
 */
const TestResultInsight: React.FC<{
  /**
   * The test result insight
   */
  currentInsight: IRcaTestResultInsight;
}> = ({ currentInsight }) => {
  const theme = useTheme();
  const { semanticColors } = theme;
  const linkColor = theme.palette.blue;
  const linkHoverColor = theme.palette.blueLight;
  const iconColors = getIconColors(theme);
  const textColor = "black";
  const boxColors: { [status: string]: string } = {
    Invalid: semanticColors.defaultStateBackground,
    Informational: semanticColors.infoBackground,
    Success: semanticColors.successBackground,
    SuccessWithWarnings: semanticColors.warningBackground,
    Warning: semanticColors.warningBackground,
    Error: semanticColors.errorBackground,
    FatalError: semanticColors.errorBackground,
    Skipped: semanticColors.defaultStateBackground,
    SuccessWithWarningsMid: semanticColors.warningBackground,
  };
  const backgroundColor = boxColors[currentInsight.Status];
  return (
    <Stack
      horizontal
      verticalAlign="start"
      styles={{
        root: {
          width: "100%",
          borderRadius: 6,
          border: "1px solid var(--rca-card-border, transparent)",
          boxShadow: `var(--rca-card-shadow, ${Depths.depth4})`,
          transition: "box-shadow 0.5s ease 0s",
          padding: 10,
          color: textColor,
          backgroundColor: backgroundColor,
          marginBottom: 10,
          selectors: {
            a: {
              color: `${linkColor} !important`,
              textDecoration: "underline",
            },
            "a:visited": {
              color: `${linkColor} !important`,
            },
            "a:hover": {
              color: `${linkHoverColor} !important`,
            },
            "a:focus": {
              color: `${linkHoverColor} !important`,
            },
          },
        },
      }}
    >
      <Stack role="img" styles={{ root: { paddingRight: 10, paddingTop: 3 } }} aria-label={insightIconLabels(currentInsight.Status)}>
        <Icon
          iconName={iconNames[currentInsight.Status]}
          styles={{
            root: { fontSize: theme.fonts.xLarge.fontSize, color: iconColors[currentInsight.Status], paddingRight: 5, paddingLeft: 5 },
          }}
        />
      </Stack>
      <Stack styles={{ root: { color: textColor } }}>
        <Stack>
          <h2 className={noMarginStyle}>
            <Text
              styles={{
                root: { fontSize: theme.fonts.large.fontSize, fontWeight: "bold", color: textColor },
              }}
            >
              {currentInsight.Title}
            </Text>
          </h2>
        </Stack>
        <Stack
          styles={{
            root: {
              paddingTop: 5,
              lineHeight: 23,
              whiteSpace: "pre-line",
              wordBreak: "normal",
              overflowWrap: "break-word",
              color: textColor,
              ...theme.fonts.medium,
            },
          }}
        >
          <ReactMarkdown components={markdownComponents}>{currentInsight.Description}</ReactMarkdown>
          {currentInsight.Actions.length > 0 ? (
            <Stack styles={{ root: { paddingTop: 5 } }}>
              <h3 className={noMarginStyle}>
                <Text
                  styles={{
                    root: { fontSize: theme.fonts.mediumPlus.fontSize, fontWeight: "bold", color: textColor },
                  }}
                >
                  {WebsiteStrings.TestResultTable_Actions}
                </Text>
              </h3>
              <ReactMarkdown components={markdownComponents}>{currentInsight.Actions}</ReactMarkdown>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};

/**
 * Function to flatten all Insights from the parent result to one list.
 *
 * @param currentResult - The result.
 * @returns - The flattened list of insights.
 */
const FlattenInsights = (currentResult: IRcaTestResult): IRcaTestResultInsight[] => {
  const insights = currentResult.Insights ?? [];
  const childInsights = (currentResult.Children ?? []).flatMap((child) => FlattenInsights(child));
  return [...insights, ...childInsights];
};
